/**
 * Consolidated GitHub Service
 *
 * Combines all GitHub-related functionality:
 * - Core API operations (branches, commits, PRs)
 * - Repository analysis
 * - Code fetching (bridge architecture)
 * - File operations
 * - Repository scaffolding
 * - Project import
 *
 * This service maintains 100% backward compatibility with existing services.
 */

import { supabase } from '../../lib/supabase';
import { logger } from '../loggingService';
import { retryWithBackoff } from '../../utils/retryWithBackoff';

// ============================================================================
// Type Definitions
// ============================================================================

export interface GitHubFile {
  path: string;
  content: string;
  encoding?: 'utf-8' | 'base64';
}

export interface GitHubCommit {
  repo: string;
  owner: string;
  branch: string;
  message: string;
  files: GitHubFile[];
}

export interface GitHubPR {
  repo: string;
  owner: string;
  head: string;
  base: string;
  title: string;
  body: string;
  draft?: boolean;
}

export interface GitHubBranch {
  repo: string;
  owner: string;
  branchName: string;
  fromBranch?: string;
}

export interface FileContent {
  path: string;
  content: string;
  size: number;
  sha: string;
  lastModified: string;
}

export interface FileTreeNode {
  id: string;
  name: string;
  type: 'file' | 'folder';
  path: string;
  children?: FileTreeNode[];
  size?: number;
  lastModified?: string;
  sha?: string;
}

export interface CodeReference {
  id: string;
  github_repo_url: string;
  github_branch: string;
  github_commit_sha?: string;
  github_file_path: string;
  artifact_type: string;
  content_hash?: string;
  functions?: any[];
  classes?: any[];
  exports?: any[];
  imports?: any[];
  metadata?: Record<string, any>;
}

export interface FetchedCode {
  content: string;
  reference: CodeReference;
  fetchedAt: string;
}

export interface TechStackAnalysis {
  frontend?: {
    framework: string;
    version?: string;
    libraries: string[];
  };
  backend?: {
    language: string;
    framework?: string;
    libraries: string[];
  };
  database?: {
    types: string[];
    orms: string[];
  };
  testing?: {
    frameworks: string[];
  };
  devTools?: {
    bundler?: string;
    linter?: string;
    formatter?: string;
  };
}

export interface ArchitectureInsights {
  projectStructure: string;
  designPatterns: string[];
  conventions: string[];
  fileOrganization: string;
}

export interface CodebaseAnalysis {
  techStack: TechStackAnalysis;
  architecture: ArchitectureInsights;
  dependencies: Record<string, string>;
  fileStructure: any[];
  standards: string[];
  totalFiles: number;
  totalLines: number;
}

// ============================================================================
// Main Service Class
// ============================================================================

export class GitHubService {
  // ==========================================================================
  // Private Helper Methods
  // ==========================================================================

  /**
   * Get GitHub access token for user
   */
  private static async getAccessToken(userId: string): Promise<string | null> {
    try {
      const { data } = await supabase
        .from('user_connections')
        .select('access_token')
        .eq('user_id', userId)
        .eq('provider', 'github')
        .in('status', ['active', 'connected'])
        .maybeSingle();

      return data?.access_token || null;
    } catch (error) {
      logger.error('Error fetching GitHub access token', { error });
      return null;
    }
  }

  /**
   * Get repository tree (raw Git tree entries)
   */
  static async getRepositoryTree(
    userId: string,
    owner: string,
    repo: string
  ): Promise<Array<{ path: string; type: string; size: number; lastModified?: string }>> {
    try {
      const repoInfo = await this.getRepository(userId, owner, repo);
      if (!repoInfo.success || !repoInfo.data) {
        throw new Error(repoInfo.error || 'Failed to get repository info');
      }

      const defaultBranch = repoInfo.data.default_branch || 'main';

      const refResult = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/ref/heads/${defaultBranch}`,
        'GET'
      );

      if (!refResult.success) {
        throw new Error(refResult.error || 'Failed to resolve branch ref');
      }

      const commitSha = refResult.data.object.sha;

      const treeResult = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/trees/${commitSha}?recursive=1`,
        'GET'
      );

      if (!treeResult.success || !Array.isArray(treeResult.data?.tree)) {
        throw new Error(treeResult.error || 'Failed to fetch repository tree');
      }

      const nowIso = new Date().toISOString();
      return treeResult.data.tree.map((item: any) => ({
        path: item.path,
        type: item.type, // 'blob' | 'tree'
        size: item.size || 0,
        lastModified: nowIso,
      }));
    } catch (error) {
      logger.error('Error in getRepositoryTree', { error, owner, repo });
      return [];
    }
  }

  /**
   * Make GitHub API request
   */
  private static async makeGitHubRequest(
    userId: string,
    endpoint: string,
    method: string = 'GET',
    body?: unknown
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    try {
      const accessToken = await this.getAccessToken(userId);

      if (!accessToken) {
        return {
          success: false,
          error: 'No GitHub access token found. Please connect GitHub.',
        };
      }

      const result = await retryWithBackoff(
        async () => {
          const response = await fetch(`https://api.github.com${endpoint}`, {
            method,
            headers: {
              Authorization: `Bearer ${accessToken}`,
              Accept: 'application/vnd.github.v3+json',
              'Content-Type': 'application/json',
            },
            body: body ? JSON.stringify(body) : undefined,
          });

          if (response.status === 429) {
            const retryAfter = response.headers.get('Retry-After');
            const delaySeconds = retryAfter ? parseInt(retryAfter, 10) : 60;
            throw new Error(
              `Rate limit exceeded. GitHub asks to retry after ${delaySeconds} seconds.`
            );
          }

          if (response.status >= 500) {
            throw new Error(
              `GitHub server error: ${response.status} ${response.statusText}`
            );
          }

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            return {
              success: false,
              error: errorData.message || `GitHub API error: ${response.status}`,
            };
          }

          const data = await response.json();
          return { success: true, data };
        },
        {
          maxRetries: 3,
          initialDelayMs: 1000,
          maxDelayMs: 10000,
          onRetry: (attempt, error) => {
            logger.warn('Retrying GitHub API request', {
              endpoint,
              attempt,
              error: error.message,
            });
          },
        }
      );

      return result;
    } catch (error) {
      logger.error('GitHub API request failed after retries', {
        endpoint,
        error: error instanceof Error ? error.message : String(error),
      });
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to communicate with GitHub API',
      };
    }
  }

  // ==========================================================================
  // Core API Operations (from githubService.ts)
  // ==========================================================================

  /**
   * Create a new branch
   */
  static async createBranch(
    userId: string,
    options: GitHubBranch
  ): Promise<{ success: boolean; branchExists?: boolean; error?: string }> {
    try {
      // Check if branch already exists
      const branchCheck = await this.makeGitHubRequest(
        userId,
        `/repos/${options.owner}/${options.repo}/git/ref/heads/${options.branchName}`,
        'GET'
      );

      if (branchCheck.success) {
        logger.info('Branch already exists, will use existing', {
          owner: options.owner,
          repo: options.repo,
          branch: options.branchName,
        });
        return { success: true, branchExists: true };
      }

      // Create branch
      const baseBranch = options.fromBranch || 'main';
      const refResult = await this.makeGitHubRequest(
        userId,
        `/repos/${options.owner}/${options.repo}/git/ref/heads/${baseBranch}`,
        'GET'
      );

      if (!refResult.success) {
        return {
          success: false,
          error: `Failed to get base branch: ${refResult.error}`,
        };
      }

      const sha = refResult.data.object.sha;
      const createResult = await this.makeGitHubRequest(
        userId,
        `/repos/${options.owner}/${options.repo}/git/refs`,
        'POST',
        {
          ref: `refs/heads/${options.branchName}`,
          sha,
        }
      );

      if (!createResult.success) {
        const errorMsg = createResult.error?.toLowerCase() || '';
        if (
          errorMsg.includes('already exists') ||
          errorMsg.includes('reference already exists')
        ) {
          logger.info('Branch created by another process, will use existing', {
            owner: options.owner,
            repo: options.repo,
            branch: options.branchName,
          });
          return { success: true, branchExists: true };
        }
        return {
          success: false,
          error: `Failed to create branch: ${createResult.error}`,
        };
      }

      return { success: true, branchExists: false };
    } catch (error) {
      logger.error('Error in createBranch', { error });
      return { success: false, error: 'Failed to create branch' };
    }
  }

  /**
   * Create or update a file
   */
  static async createOrUpdateFile(
    userId: string,
    repo: string,
    owner: string,
    branch: string,
    filePath: string,
    content: string,
    message: string
  ): Promise<{ success: boolean; commitSha?: string; error?: string }> {
    try {
      const fileResult = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/contents/${filePath}?ref=${branch}`,
        'GET'
      );

      const encodedContent = Buffer.from(content).toString('base64');
      const body: any = {
        message,
        content: encodedContent,
        branch,
      };

      if (fileResult.success && fileResult.data.sha) {
        body.sha = fileResult.data.sha;
      }

      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/contents/${filePath}`,
        'PUT',
        body
      );

      if (!result.success) {
        return {
          success: false,
          error: `Failed to create/update file: ${result.error}`,
        };
      }

      const commitSha = result.data?.commit?.sha || undefined;
      return { success: true, commitSha };
    } catch (error) {
      logger.error('Error in createOrUpdateFile', { error });
      return { success: false, error: 'Failed to create/update file' };
    }
  }

  /**
   * Commit multiple files
   */
  static async commitMultipleFiles(
    userId: string,
    options: GitHubCommit
  ): Promise<{ success: boolean; commitSha?: string; error?: string }> {
    try {
      let lastCommitSha: string | undefined;

      for (const file of options.files) {
        const result = await this.createOrUpdateFile(
          userId,
          options.repo,
          options.owner,
          options.branch,
          file.path,
          file.content,
          options.message
        );

        if (!result.success) {
          return {
            success: false,
            error: `Failed to commit ${file.path}: ${result.error}`,
          };
        }

        if (result.commitSha) {
          lastCommitSha = result.commitSha;
        }
      }

      if (!lastCommitSha) {
        const branchResult = await this.makeGitHubRequest(
          userId,
          `/repos/${options.owner}/${options.repo}/git/ref/heads/${options.branch}`,
          'GET'
        );

        if (branchResult.success && branchResult.data?.object?.sha) {
          lastCommitSha = branchResult.data.object.sha;
        }
      }

      return { success: true, commitSha: lastCommitSha };
    } catch (error) {
      logger.error('Error in commitMultipleFiles', { error });
      return { success: false, error: 'Failed to commit files' };
    }
  }

  /**
   * Create a pull request
   */
  static async createPullRequest(
    userId: string,
    options: GitHubPR
  ): Promise<{
    success: boolean;
    prNumber?: number;
    prUrl?: string;
    error?: string;
  }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${options.owner}/${options.repo}/pulls`,
        'POST',
        {
          title: options.title,
          head: options.head,
          base: options.base,
          body: options.body,
          draft: options.draft || false,
        }
      );

      if (!result.success) {
        return {
          success: false,
          error: `Failed to create PR: ${result.error}`,
        };
      }

      return {
        success: true,
        prNumber: result.data.number,
        prUrl: result.data.html_url,
      };
    } catch (error) {
      logger.error('Error in createPullRequest', { error });
      return { success: false, error: 'Failed to create pull request' };
    }
  }

  /**
   * Merge a pull request
   */
  static async mergePullRequest(
    userId: string,
    repo: string,
    owner: string,
    prNumber: number,
    mergeMethod: 'merge' | 'squash' | 'rebase' = 'squash'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/pulls/${prNumber}/merge`,
        'PUT',
        {
          merge_method: mergeMethod,
        }
      );

      if (!result.success) {
        return { success: false, error: `Failed to merge PR: ${result.error}` };
      }

      return { success: true };
    } catch (error) {
      logger.error('Error in mergePullRequest', { error });
      return { success: false, error: 'Failed to merge pull request' };
    }
  }

  /**
   * Delete a branch
   */
  static async deleteBranch(
    userId: string,
    repo: string,
    owner: string,
    branchName: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/refs/heads/${branchName}`,
        'DELETE'
      );

      if (!result.success) {
        return {
          success: false,
          error: `Failed to delete branch: ${result.error}`,
        };
      }

      return { success: true };
    } catch (error) {
      logger.error('Error in deleteBranch', { error });
      return { success: false, error: 'Failed to delete branch' };
    }
  }

  /**
   * Get repository information
   */
  static async getRepository(
    userId: string,
    owner: string,
    repo: string
  ): Promise<{ success: boolean; data?: any; error?: string }> {
    return this.makeGitHubRequest(userId, `/repos/${owner}/${repo}`, 'GET');
  }

  /**
   * List branches
   */
  static async listBranches(
    userId: string,
    owner: string,
    repo: string
  ): Promise<{ success: boolean; branches?: string[]; error?: string }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/branches`,
        'GET'
      );

      if (!result.success) {
        return { success: false, error: result.error };
      }

      const branches = result.data.map((branch: any) => branch.name);
      return { success: true, branches };
    } catch (error) {
      logger.error('Error in listBranches', { error });
      return { success: false, error: 'Failed to list branches' };
    }
  }

  /**
   * Get file content
   */
  static async getFileContent(
    userId: string,
    owner: string,
    repo: string,
    path: string,
    ref?: string
  ): Promise<{ success: boolean; content?: string; error?: string }> {
    try {
      const endpoint = `/repos/${owner}/${repo}/contents/${path}${ref ? `?ref=${ref}` : ''}`;
      const result = await this.makeGitHubRequest(userId, endpoint, 'GET');

      if (!result.success) {
        return { success: false, error: result.error };
      }

      const content = Buffer.from(result.data.content, 'base64').toString(
        'utf-8'
      );
      return { success: true, content };
    } catch (error) {
      logger.error('Error in getFileContent', { error });
      return { success: false, error: 'Failed to get file content' };
    }
  }

  /**
   * Update file
   */
  static async updateFile(
    userId: string,
    owner: string,
    repo: string,
    path: string,
    content: string,
    message: string,
    sha: string,
    branch: string = 'main'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/contents/${path}`,
        'PUT',
        {
          message,
          content: Buffer.from(content).toString('base64'),
          sha,
          branch,
        }
      );

      if (!result.success) {
        return { success: false, error: result.error };
      }

      return { success: true };
    } catch (error) {
      logger.error('Error in updateFile', { error });
      return { success: false, error: 'Failed to update file' };
    }
  }

  /**
   * Create file
   */
  static async createFile(
    userId: string,
    owner: string,
    repo: string,
    path: string,
    content: string,
    message: string,
    branch: string = 'main'
  ): Promise<{ success: boolean; error?: string }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/contents/${path}`,
        'PUT',
        {
          message,
          content: Buffer.from(content).toString('base64'),
          branch,
        }
      );

      if (!result.success) {
        return { success: false, error: result.error };
      }

      return { success: true };
    } catch (error) {
      logger.error('Error in createFile', { error });
      return { success: false, error: 'Failed to create file' };
    }
  }

  /**
   * Parse repository URL
   */
  static parseRepoUrl(repoUrl: string): { owner: string; repo: string } | null {
    try {
      const match = repoUrl.match(/github\.com\/([^/]+)\/([^/.]+)/);
      if (!match) return null;

      return {
        owner: match[1],
        repo: match[2],
      };
    } catch (error) {
      logger.error('Error parsing repo URL', { error });
      return null;
    }
  }

  /**
   * Generate branch name
   */
  static generateBranchName(ticketKey: string, phaseName: string): string {
    const sanitizedPhase = phaseName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    return `cyclone/${ticketKey}/${sanitizedPhase}`;
  }

  /**
   * Generate commit message
   */
  static generateCommitMessage(
    ticketKey: string,
    phaseName: string,
    agentName: string,
    filesChanged: number
  ): string {
    return `[${ticketKey}] ${phaseName} by ${agentName}\n\nFiles changed: ${filesChanged}\nAutomated by Cyclone Platform`;
  }

  /**
   * Generate PR body
   */
  static generatePRBody(
    ticketTitle: string,
    ticketDescription: string,
    phaseResults: Array<{ phase: string; status: string; outputs: any }>,
    policyChecksPassed: boolean
  ): string {
    let body = `## ${ticketTitle}\n\n`;
    body += `${ticketDescription}\n\n`;
    body += `---\n\n`;
    body += `### SDLC Phases Completed\n\n`;

    phaseResults.forEach(result => {
      const icon = result.status === 'completed' ? '✅' : '❌';
      body += `${icon} **${result.phase}**: ${result.status}\n`;
    });

    body += `\n---\n\n`;
    body += `### Policy Checks\n\n`;
    body += policyChecksPassed
      ? '✅ All policy checks passed\n'
      : '❌ Some policy checks failed - review required\n';

    body += `\n---\n\n`;
    body += `_Automated by [Cyclone Platform](https://cyclone.dev)_\n`;

    return body;
  }

  /**
   * Sync from GitHub
   */
  static async syncFromGitHub(
    userId: string,
    projectId: string,
    owner: string,
    repo: string,
    branch: string = 'main'
  ): Promise<{ success: boolean; filesUpdated?: number; error?: string }> {
    try {
      const refResult = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/ref/heads/${branch}`,
        'GET'
      );

      if (!refResult.success) {
        return {
          success: false,
          error: `Failed to get branch ref: ${refResult.error}`,
        };
      }

      const commitSha = refResult.data.object.sha;
      const treeResult = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/trees/${commitSha}?recursive=1`,
        'GET'
      );

      if (!treeResult.success) {
        return {
          success: false,
          error: `Failed to get file tree: ${treeResult.error}`,
        };
      }

      await supabase
        .from('projects')
        .update({
          last_github_sync: new Date().toISOString(),
          github_commit_sha: commitSha,
        })
        .eq('id', projectId);

      logger.info('GitHub sync completed', {
        projectId,
        owner,
        repo,
        branch,
        commitSha,
      });

      return { success: true, filesUpdated: treeResult.data.tree?.length || 0 };
    } catch (error) {
      logger.error('GitHub sync failed', { error, projectId });
      return { success: false, error: 'Failed to sync from GitHub' };
    }
  }

  /**
   * Push to GitHub
   */
  static async pushToGitHub(
    userId: string,
    projectId: string,
    owner: string,
    repo: string,
    branch: string,
    files: GitHubFile[],
    message: string
  ): Promise<{ success: boolean; commitSha?: string; error?: string }> {
    try {
      const result = await this.commitMultipleFiles(userId, {
        repo,
        owner,
        branch,
        message,
        files,
      });

      if (!result.success) {
        return { success: false, error: result.error };
      }

      await supabase
        .from('projects')
        .update({
          last_github_push: new Date().toISOString(),
        })
        .eq('id', projectId);

      logger.info('GitHub push completed', {
        projectId,
        owner,
        repo,
        branch,
        filesCount: files.length,
      });

      return { success: true, commitSha: result.commitSha };
    } catch (error) {
      logger.error('GitHub push failed', { error, projectId });
      return { success: false, error: 'Failed to push to GitHub' };
    }
  }

  /**
   * Handle webhook events - now triggers autonomous blueprint updates
   */
  static async handleWebhook(
    event: string,
    payload: any,
    projectId: string
  ): Promise<{ success: boolean; error?: string }> {
    try {
      logger.info('GitHub webhook received', { event, projectId });

      switch (event) {
        case 'push':
          // Update project with latest commit
          await supabase
            .from('projects')
            .update({
              last_github_sync: new Date().toISOString(),
              github_commit_sha: payload.after,
            })
            .eq('id', projectId);
          
          // Trigger autonomous blueprint update from code changes
          await this.analyzeAndUpdateBlueprint(projectId, payload);
          break;

        case 'pull_request': {
          const prNumber = payload.pull_request?.number;
          const action = payload.action;

          await supabase.from('pull_requests').upsert({
            project_id: projectId,
            pr_number: prNumber,
            title: payload.pull_request?.title,
            status:
              action === 'closed' && payload.pull_request?.merged
                ? 'merged'
                : action,
            url: payload.pull_request?.html_url,
            updated_at: new Date().toISOString(),
          });
          
          // If PR merged, update blueprint with new features
          if (action === 'closed' && payload.pull_request?.merged) {
            await this.syncMergedPRToBlueprint(projectId, payload.pull_request);
          }
          break;
        }

        case 'issues':
          logger.info('GitHub issue event', {
            action: payload.action,
            issueNumber: payload.issue?.number,
          });
          
          // Convert issues to requirements in blueprint
          if (payload.action === 'opened' || payload.action === 'edited') {
            await this.syncIssueToBlueprint(projectId, payload.issue);
          }
          break;

        default:
          logger.warn('Unhandled GitHub webhook event', { event });
      }

      return { success: true };
    } catch (error) {
      logger.error('GitHub webhook handling failed', {
        error,
        event,
        projectId,
      });
      return { success: false, error: 'Failed to handle webhook' };
    }
  }

  /**
   * Deep repository analysis for blueprint generation
   * Analyzes entire codebase structure, dependencies, and patterns
   */
  static async analyzeRepositoryForBlueprint(
    userId: string,
    owner: string,
    repo: string,
    branch = 'main'
  ): Promise<{
    structure: any;
    features: string[];
    requirements: string[];
    techStack: any;
    architecture: any;
    confidence: number;
  }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      if (!accessToken) {
        throw new Error('No GitHub access token found');
      }

      const octokit = new Octokit({ auth: accessToken });

      // Get repository tree
      const { data: tree } = await octokit.rest.git.getTree({
        owner,
        repo,
        tree_sha: branch,
        recursive: 'true',
      });

      // Analyze file structure
      const structure = this.analyzeFileStructure(tree.tree);
      
      // Get and analyze key files
      const keyFiles = await this.getKeyFiles(octokit, owner, repo, tree.tree);
      
      // Extract tech stack from package.json, requirements.txt, etc.
      const techStack = this.extractTechStack(keyFiles);
      
      // Analyze architecture from code structure
      const architecture = this.inferArchitecture(structure, keyFiles);
      
      // Extract features from README and code comments
      const features = await this.extractFeatures(octokit, owner, repo, keyFiles);
      
      // Extract requirements from issues and docs
      const requirements = await this.extractRequirements(octokit, owner, repo);
      
      // Calculate confidence based on completeness
      const confidence = this.calculateAnalysisConfidence({
        structure,
        techStack,
        architecture,
        features,
        requirements,
      });

      return {
        structure,
        features,
        requirements,
        techStack,
        architecture,
        confidence,
      };
    } catch (error) {
      logger.error('Repository analysis failed', { error, owner, repo });
      throw error;
    }
  }

  /**
   * Analyze file structure to understand project organization
   */
  private static analyzeFileStructure(tree: any[]): any {
    const structure: any = {
      directories: {},
      fileTypes: {},
      patterns: [],
    };

    tree.forEach(item => {
      if (item.type === 'tree') {
        const parts = item.path.split('/');
        const topLevel = parts[0];
        structure.directories[topLevel] = (structure.directories[topLevel] || 0) + 1;
      } else if (item.type === 'blob') {
        const ext = item.path.split('.').pop();
        structure.fileTypes[ext] = (structure.fileTypes[ext] || 0) + 1;
      }
    });

    // Detect common patterns
    if (structure.directories['src']) structure.patterns.push('src-based');
    if (structure.directories['components']) structure.patterns.push('component-based');
    if (structure.directories['pages']) structure.patterns.push('page-based');
    if (structure.directories['api']) structure.patterns.push('api-routes');
    if (structure.fileTypes['ts'] || structure.fileTypes['tsx']) structure.patterns.push('typescript');
    if (structure.fileTypes['js'] || structure.fileTypes['jsx']) structure.patterns.push('javascript');

    return structure;
  }

  /**
   * Get and analyze key configuration and code files
   */
  private static async getKeyFiles(octokit: Octokit, owner: string, repo: string, tree: any[]): Promise<any> {
    const keyFiles: any = {};
    const filesToFetch = [
      'package.json',
      'requirements.txt',
      'Dockerfile',
      'docker-compose.yml',
      '.env.example',
      'README.md',
      'tsconfig.json',
      'webpack.config.js',
      'next.config.js',
    ];

    for (const fileName of filesToFetch) {
      const file = tree.find(item => item.path === fileName);
      if (file) {
        try {
          const { data } = await octokit.rest.repos.getContent({
            owner,
            repo,
            path: fileName,
          });
          
          if ('content' in data) {
            keyFiles[fileName] = Buffer.from(data.content, 'base64').toString('utf-8');
          }
        } catch (error) {
          logger.debug(`Could not fetch ${fileName}`, { error });
        }
      }
    }

    return keyFiles;
  }

  /**
   * Extract tech stack from configuration files
   */
  private static extractTechStack(keyFiles: any): any {
    const techStack: any = {
      frontend: [],
      backend: [],
      database: [],
      infrastructure: [],
      testing: [],
    };

    // Analyze package.json
    if (keyFiles['package.json']) {
      try {
        const pkg = JSON.parse(keyFiles['package.json']);
        const deps = { ...pkg.dependencies, ...pkg.devDependencies };
        
        // Frontend frameworks
        if (deps['react']) techStack.frontend.push('React');
        if (deps['vue']) techStack.frontend.push('Vue');
        if (deps['angular']) techStack.frontend.push('Angular');
        if (deps['next']) techStack.frontend.push('Next.js');
        
        // Backend frameworks
        if (deps['express']) techStack.backend.push('Express');
        if (deps['fastify']) techStack.backend.push('Fastify');
        if (deps['nestjs']) techStack.backend.push('NestJS');
        
        // Databases
        if (deps['mongoose']) techStack.database.push('MongoDB');
        if (deps['pg']) techStack.database.push('PostgreSQL');
        if (deps['mysql']) techStack.database.push('MySQL');
        
        // Testing
        if (deps['jest']) techStack.testing.push('Jest');
        if (deps['mocha']) techStack.testing.push('Mocha');
        if (deps['cypress']) techStack.testing.push('Cypress');
      } catch (error) {
        logger.debug('Could not parse package.json', { error });
      }
    }

    // Analyze requirements.txt for Python projects
    if (keyFiles['requirements.txt']) {
      const lines = keyFiles['requirements.txt'].split('\n');
      if (lines.some(l => l.includes('django'))) techStack.backend.push('Django');
      if (lines.some(l => l.includes('flask'))) techStack.backend.push('Flask');
      if (lines.some(l => l.includes('fastapi'))) techStack.backend.push('FastAPI');
    }

    return techStack;
  }

  /**
   * Infer architecture from code structure and configuration
   */
  private static inferArchitecture(structure: any, keyFiles: any): any {
    const architecture: any = {
      type: 'unknown',
      layers: {},
      patterns: [],
    };

    // Determine architecture type
    if (structure.patterns.includes('component-based') && structure.patterns.includes('page-based')) {
      architecture.type = 'spa'; // Single Page Application
    } else if (structure.directories['api'] && structure.directories['client']) {
      architecture.type = 'fullstack';
    } else if (structure.directories['api'] || structure.directories['routes']) {
      architecture.type = 'api';
    }

    // Identify layers
    if (structure.directories['components'] || structure.directories['views']) {
      architecture.layers.presentation = true;
    }
    if (structure.directories['services'] || structure.directories['controllers']) {
      architecture.layers.business = true;
    }
    if (structure.directories['models'] || structure.directories['entities']) {
      architecture.layers.data = true;
    }

    // Identify patterns
    if (keyFiles['Dockerfile']) architecture.patterns.push('containerized');
    if (keyFiles['docker-compose.yml']) architecture.patterns.push('microservices');
    if (structure.directories['__tests__'] || structure.directories['test']) {
      architecture.patterns.push('test-driven');
    }

    return architecture;
  }

  /**
   * Extract features from README and code
   */
  private static async extractFeatures(octokit: Octokit, owner: string, repo: string, keyFiles: any): Promise<string[]> {
    const features: string[] = [];

    // Extract from README
    if (keyFiles['README.md']) {
      const readme = keyFiles['README.md'];
      const featureSection = readme.match(/##?\s*Features([\s\S]*?)##/i);
      if (featureSection) {
        const lines = featureSection[1].split('\n');
        lines.forEach(line => {
          const match = line.match(/[-*]\s+(.+)/);
          if (match) features.push(match[1].trim());
        });
      }
    }

    // Get features from recent commits
    try {
      const { data: commits } = await octokit.rest.repos.listCommits({
        owner,
        repo,
        per_page: 20,
      });

      commits.forEach(commit => {
        const message = commit.commit.message.toLowerCase();
        if (message.includes('feat:') || message.includes('feature:')) {
          const feature = message.split(/feat:|feature:/)[1].split('\n')[0].trim();
          if (feature) features.push(feature);
        }
      });
    } catch (error) {
      logger.debug('Could not fetch commits', { error });
    }

    return [...new Set(features)]; // Remove duplicates
  }

  /**
   * Extract requirements from issues and documentation
   */
  private static async extractRequirements(octokit: Octokit, owner: string, repo: string): Promise<string[]> {
    const requirements: string[] = [];

    try {
      // Get issues labeled as requirements or features
      const { data: issues } = await octokit.rest.issues.listForRepo({
        owner,
        repo,
        labels: 'requirement,feature,enhancement',
        state: 'all',
        per_page: 50,
      });

      issues.forEach(issue => {
        requirements.push(issue.title);
      });
    } catch (error) {
      logger.debug('Could not fetch issues', { error });
    }

    return requirements;
  }

  /**
   * Calculate confidence score for the analysis
   */
  private static calculateAnalysisConfidence(analysis: any): number {
    let score = 0;
    let total = 0;

    // Check structure completeness
    if (Object.keys(analysis.structure.directories).length > 0) score += 20;
    total += 20;

    // Check tech stack detection
    const techStackCount = Object.values(analysis.techStack).flat().length;
    if (techStackCount > 0) score += Math.min(20, techStackCount * 4);
    total += 20;

    // Check architecture inference
    if (analysis.architecture.type !== 'unknown') score += 15;
    if (Object.keys(analysis.architecture.layers).length > 0) score += 15;
    total += 30;

    // Check features and requirements
    if (analysis.features.length > 0) score += 15;
    if (analysis.requirements.length > 0) score += 15;
    total += 30;

    return score / total;
  }

  /**
   * Automatically update blueprint when code changes are pushed
   */
  private static async analyzeAndUpdateBlueprint(projectId: string, pushPayload: any): Promise<void> {
    try {
      // Get project and blueprint
      const { data: project } = await supabase
        .from('projects')
        .select('*, project_blueprints(*)')
        .eq('id', projectId)
        .single();

      if (!project || !project.project_blueprints?.[0]) {
        logger.warn('No blueprint found for project', { projectId });
        return;
      }

      const blueprint = project.project_blueprints[0];
      
      // Skip if blueprint is locked
      if (blueprint.is_locked) {
        logger.info('Blueprint is locked, skipping auto-update', { projectId });
        return;
      }

      // Analyze changed files
      const changedFiles = pushPayload.commits?.flatMap((c: any) => [
        ...c.added,
        ...c.modified,
      ]) || [];

      // Update blueprint based on changes
      const updates: any = {};
      
      // Check for new API routes
      const apiFiles = changedFiles.filter((f: string) => f.includes('api/') || f.includes('routes/'));
      if (apiFiles.length > 0) {
        updates.newApis = apiFiles;
      }

      // Check for new components
      const componentFiles = changedFiles.filter((f: string) => f.includes('components/'));
      if (componentFiles.length > 0) {
        updates.newComponents = componentFiles;
      }

      // Check for schema changes
      const schemaFiles = changedFiles.filter((f: string) => f.includes('schema') || f.includes('models/'));
      if (schemaFiles.length > 0) {
        updates.schemaChanges = schemaFiles;
      }

      if (Object.keys(updates).length > 0) {
        // Use AI to understand the changes and update blueprint
        const { AIOrchestratorService } = await import('../ai/AIOrchestratorService');
        await AIOrchestratorService.orchestrate({
          type: 'blueprint_analysis',
          projectId,
          userId: project.user_id,
          data: {
            blueprintId: blueprint.id,
            codeChanges: updates,
          },
        });
      }
    } catch (error) {
      logger.error('Failed to update blueprint from push', { error, projectId });
    }
  }

  /**
   * Sync merged PR changes to blueprint
   */
  private static async syncMergedPRToBlueprint(projectId: string, pullRequest: any): Promise<void> {
    try {
      const { data: project } = await supabase
        .from('projects')
        .select('*, project_blueprints(*)')
        .eq('id', projectId)
        .single();

      if (!project || !project.project_blueprints?.[0]) {
        return;
      }

      const blueprint = project.project_blueprints[0];
      const content = blueprint.content || {};

      // Add PR as completed feature
      if (!content.completedFeatures) content.completedFeatures = [];
      content.completedFeatures.push({
        id: `pr-${pullRequest.number}`,
        title: pullRequest.title,
        description: pullRequest.body,
        mergedAt: new Date().toISOString(),
        url: pullRequest.html_url,
      });

      // Update blueprint
      await supabase
        .from('project_blueprints')
        .update({
          content,
          updated_at: new Date().toISOString(),
        })
        .eq('id', blueprint.id);

    } catch (error) {
      logger.error('Failed to sync PR to blueprint', { error, projectId });
    }
  }

  /**
   * Convert GitHub issues to blueprint requirements
   */
  private static async syncIssueToBlueprint(projectId: string, issue: any): Promise<void> {
    try {
      const { data: project } = await supabase
        .from('projects')
        .select('*, project_blueprints(*)')
        .eq('id', projectId)
        .single();

      if (!project || !project.project_blueprints?.[0]) {
        return;
      }

      const blueprint = project.project_blueprints[0];
      const content = blueprint.content || {};

      // Add issue as requirement
      if (!content.requirements) content.requirements = [];
      
      // Check if issue already exists
      const existingIndex = content.requirements.findIndex((r: any) => r.githubIssueNumber === issue.number);
      
      if (existingIndex >= 0) {
        // Update existing
        content.requirements[existingIndex] = {
          ...content.requirements[existingIndex],
          title: issue.title,
          description: issue.body,
          status: issue.state,
          updatedAt: new Date().toISOString(),
        };
      } else {
        // Add new
        content.requirements.push({
          id: `issue-${issue.number}`,
          githubIssueNumber: issue.number,
          title: issue.title,
          description: issue.body,
          status: issue.state,
          labels: issue.labels?.map((l: any) => l.name) || [],
          createdAt: issue.created_at,
          url: issue.html_url,
        });
      }

      // Update blueprint
      await supabase
        .from('project_blueprints')
        .update({
          content,
          updated_at: new Date().toISOString(),
        })
        .eq('id', blueprint.id);

    } catch (error) {
      logger.error('Failed to sync issue to blueprint', { error, projectId });
    }
  }

  /**
   * Get commit details
   */
  static async getCommit(
    userId: string,
    owner: string,
    repo: string,
    sha: string
  ): Promise<{ success: boolean; commit?: any; error?: string }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/commits/${sha}`,
        'GET'
      );

      if (!result.success) {
        return { success: false, error: result.error };
      }

      return { success: true, commit: result.data };
    } catch (error) {
      logger.error('Error fetching commit', {
        error,
        userId,
        owner,
        repo,
        sha,
      });
      return { success: false, error: 'Failed to fetch commit from GitHub' };
    }
  }

  /**
   * Get pull request details
   */
  static async getPullRequest(
    userId: string,
    owner: string,
    repo: string,
    prNumber: number
  ): Promise<{ success: boolean; pr?: any; error?: string }> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/pulls/${prNumber}`,
        'GET'
      );

      if (!result.success) {
        return { success: false, error: result.error };
      }

      return { success: true, pr: result.data };
    } catch (error) {
      logger.error('Error fetching pull request', {
        error,
        userId,
        owner,
        repo,
        prNumber,
      });
      return {
        success: false,
        error: 'Failed to fetch pull request from GitHub',
      };
    }
  }

  // ==========================================================================
  // File Operations (from githubFileService.ts)
  // ==========================================================================

  /**
   * Get file tree
   */
  static async getFileTree(
    userId: string,
    repoFullName: string,
    branch: string = 'main'
  ): Promise<FileTreeNode[]> {
    try {
      const [owner, repo] = repoFullName.split('/');
      if (!owner || !repo) {
        throw new Error('Invalid repository format. Expected: owner/repo');
      }

      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/trees/${branch}?recursive=1`,
        'GET'
      );

      if (!result.success || !result.data?.tree) {
        throw new Error(result.error || 'Failed to fetch file tree');
      }

      return this.buildFileTree(result.data.tree);
    } catch (error) {
      logger.error('Error fetching file tree', {
        error,
        userId,
        repoFullName,
        branch,
      });
      throw error;
    }
  }

  /**
   * Get file content (enhanced version with metadata)
   */
  static async getFileContentWithMetadata(
    userId: string,
    repoFullName: string,
    filePath: string,
    branch: string = 'main'
  ): Promise<FileContent> {
    try {
      const [owner, repo] = repoFullName.split('/');
      if (!owner || !repo) {
        throw new Error('Invalid repository format. Expected: owner/repo');
      }

      const result = await this.getFileContent(
        userId,
        owner,
        repo,
        filePath,
        branch
      );

      if (!result.success || !result.content) {
        throw new Error(result.error || 'Failed to fetch file content');
      }

      // Get file metadata
      const metadataResult = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/contents/${encodeURIComponent(filePath)}?ref=${branch}`,
        'GET'
      );

      return {
        path: filePath,
        content: result.content,
        size: metadataResult.data?.size || 0,
        sha: metadataResult.data?.sha || '',
        lastModified:
          metadataResult.data?.last_modified || new Date().toISOString(),
      };
    } catch (error) {
      logger.error('Error fetching file content with metadata:', error);
      throw error;
    }
  }

  /**
   * Delete file
   */
  static async deleteFile(
    userId: string,
    repoFullName: string,
    filePath: string,
    message: string,
    branch: string = 'main'
  ): Promise<void> {
    try {
      const [owner, repo] = repoFullName.split('/');
      if (!owner || !repo) {
        throw new Error('Invalid repository format. Expected: owner/repo');
      }

      const currentFile = await this.getFileContentWithMetadata(
        userId,
        repoFullName,
        filePath,
        branch
      );

      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/contents/${encodeURIComponent(filePath)}`,
        'DELETE',
        {
          message,
          sha: currentFile.sha,
          branch,
        }
      );

      if (!result.success) {
        throw new Error(result.error || 'Failed to delete file');
      }
    } catch (error) {
      logger.error('Error deleting file:', error);
      throw error;
    }
  }

  /**
   * Get repository info
   */
  static async getRepoInfo(
    userId: string,
    repoFullName: string
  ): Promise<{ owner: string; repo: string; defaultBranch: string }> {
    try {
      const [owner, repo] = repoFullName.split('/');
      if (!owner || !repo) {
        throw new Error('Invalid repository format. Expected: owner/repo');
      }

      const result = await this.getRepository(userId, owner, repo);

      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to fetch repository info');
      }

      return {
        owner: result.data.owner.login,
        repo: result.data.name,
        defaultBranch: result.data.default_branch,
      };
    } catch (error) {
      logger.error('Error fetching repo info:', error);
      throw error;
    }
  }

  /**
   * Build file tree structure
   */
  private static buildFileTree(tree: any[]): FileTreeNode[] {
    const nodeMap = new Map<string, FileTreeNode>();
    const rootNodes: FileTreeNode[] = [];

    // First pass: create all nodes
    tree.forEach(item => {
      const pathParts = item.path.split('/');
      const name = pathParts[pathParts.length - 1];
      const type = item.type === 'tree' ? 'folder' : 'file';

      const node: FileTreeNode = {
        id: item.path,
        name,
        type,
        path: `/${item.path}`,
        size: item.size,
        lastModified: item.last_modified,
        sha: item.sha,
      };

      nodeMap.set(item.path, node);
    });

    // Second pass: build hierarchy
    tree.forEach(item => {
      const node = nodeMap.get(item.path);
      if (!node) return;

      const pathParts = item.path.split('/');

      if (pathParts.length === 1) {
        rootNodes.push(node);
      } else {
        const parentPath = pathParts.slice(0, -1).join('/');
        const parent = nodeMap.get(parentPath);

        if (parent) {
          if (!parent.children) {
            parent.children = [];
          }
          parent.children.push(node);
        }
      }
    });

    return rootNodes;
  }

  // ==========================================================================
  // Code Fetching (from githubCodeFetchService.ts - Bridge Architecture)
  // ==========================================================================

  /**
   * Fetch code from artifact reference (bridge architecture)
   */
  static async fetchCodeFromReference(
    userId: string,
    artifactId: string
  ): Promise<{ success: boolean; code?: FetchedCode; error?: string }> {
    try {
      const { data: artifact, error } = await supabase
        .from('code_artifacts')
        .select('*')
        .eq('id', artifactId)
        .single();

      if (error || !artifact) {
        return { success: false, error: 'Artifact reference not found' };
      }

      if (!artifact.github_repo_url || !artifact.github_file_path) {
        return {
          success: false,
          error:
            'Artifact missing GitHub reference. This is a legacy artifact without bridge architecture.',
        };
      }

      const repoInfo = this.parseRepoUrl(artifact.github_repo_url);
      if (!repoInfo) {
        return { success: false, error: 'Invalid GitHub repository URL' };
      }

      const fileContent = await this.fetchFileFromGitHub(
        userId,
        repoInfo.owner,
        repoInfo.repo,
        artifact.github_file_path,
        artifact.github_branch || 'main',
        artifact.github_commit_sha
      );

      if (!fileContent.success) {
        return { success: false, error: fileContent.error };
      }

      return {
        success: true,
        code: {
          content: fileContent.content!,
          reference: artifact as CodeReference,
          fetchedAt: new Date().toISOString(),
        },
      };
    } catch (error) {
      logger.error('Error fetching code from reference', { error, artifactId });
      return { success: false, error: 'Failed to fetch code from GitHub' };
    }
  }

  /**
   * Fetch file from GitHub
   */
  static async fetchFileFromGitHub(
    userId: string,
    owner: string,
    repo: string,
    filePath: string,
    branch: string = 'main',
    commitSha?: string
  ): Promise<{ success: boolean; content?: string; error?: string }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      if (!accessToken) {
        return { success: false, error: 'GitHub connection not found' };
      }

      let url = `https://api.github.com/repos/${owner}/${repo}/contents/${filePath}`;
      if (commitSha) {
        url += `?ref=${commitSha}`;
      } else {
        url += `?ref=${branch}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/vnd.github.v3+json',
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        logger.error('GitHub API error', {
          status: response.status,
          error: errorText,
        });
        return {
          success: false,
          error: `GitHub API error: ${response.status} ${errorText}`,
        };
      }

      const data = await response.json();

      if (data.content && data.encoding === 'base64') {
        const content = Buffer.from(data.content, 'base64').toString('utf-8');
        return { success: true, content };
      }

      return { success: false, error: 'File content not found' };
    } catch (error) {
      logger.error('Error fetching file from GitHub', { error });
      return { success: false, error: 'Failed to fetch file from GitHub' };
    }
  }

  /**
   * Register artifact reference (bridge architecture)
   */
  static async registerArtifactReference(
    projectId: string,
    ticketId: string,
    agentId: string,
    options: {
      github_repo_url: string;
      github_branch: string;
      github_commit_sha: string;
      github_file_path: string;
      artifact_type: string;
      content_hash?: string;
      functions?: any[];
      classes?: any[];
      exports?: any[];
      imports?: any[];
      metadata?: Record<string, any>;
    }
  ): Promise<{ success: boolean; artifactId?: string; error?: string }> {
    try {
      const { data, error } = await supabase
        .from('code_artifacts')
        .insert({
          project_id: projectId,
          ticket_id: ticketId,
          agent_id: agentId,
          github_repo_url: options.github_repo_url,
          github_branch: options.github_branch,
          github_commit_sha: options.github_commit_sha,
          github_file_path: options.github_file_path,
          artifact_type: options.artifact_type,
          content_hash: options.content_hash,
          functions: options.functions || [],
          classes: options.classes || [],
          exports: options.exports || [],
          imports: options.imports || [],
          metadata: options.metadata || {},
          is_active: true,
        })
        .select('id')
        .single();

      if (error) {
        logger.error('Error registering artifact reference', { error });
        return { success: false, error: error.message };
      }

      logger.info('Artifact reference registered', {
        artifactId: data.id,
        filePath: options.github_file_path,
      });

      return { success: true, artifactId: data.id };
    } catch (error) {
      logger.error('Error in registerArtifactReference', { error });
      return { success: false, error: 'Failed to register artifact reference' };
    }
  }

  /**
   * Batch fetch multiple files
   */
  static async fetchMultipleFiles(
    userId: string,
    references: Array<{
      owner: string;
      repo: string;
      filePath: string;
      branch?: string;
      commitSha?: string;
    }>
  ): Promise<{
    success: boolean;
    files?: Array<{ path: string; content: string }>;
    errors?: Array<{ path: string; error: string }>;
  }> {
    const files: Array<{ path: string; content: string }> = [];
    const errors: Array<{ path: string; error: string }> = [];

    for (const ref of references) {
      const result = await this.fetchFileFromGitHub(
        userId,
        ref.owner,
        ref.repo,
        ref.filePath,
        ref.branch || 'main',
        ref.commitSha
      );

      if (result.success && result.content) {
        files.push({ path: ref.filePath, content: result.content });
      } else {
        errors.push({
          path: ref.filePath,
          error: result.error || 'Unknown error',
        });
      }
    }

    return {
      success: errors.length === 0,
      files: files.length > 0 ? files : undefined,
      errors: errors.length > 0 ? errors : undefined,
    };
  }

  /**
   * Get artifact references
   */
  static async getArtifactReferences(
    projectId: string,
    filters?: {
      ticketId?: string;
      agentId?: string;
      artifactType?: string;
    }
  ): Promise<{
    success: boolean;
    artifacts?: CodeReference[];
    error?: string;
  }> {
    try {
      let query = supabase
        .from('code_artifacts')
        .select('*')
        .eq('project_id', projectId)
        .eq('is_active', true);

      if (filters?.ticketId) {
        query = query.eq('ticket_id', filters.ticketId);
      }

      if (filters?.agentId) {
        query = query.eq('agent_id', filters.agentId);
      }

      if (filters?.artifactType) {
        query = query.eq('artifact_type', filters.artifactType);
      }

      const { data, error } = await query;

      if (error) {
        return { success: false, error: error.message };
      }

      return { success: true, artifacts: data as CodeReference[] };
    } catch (error) {
      logger.error('Error getting artifact references', { error });
      return { success: false, error: 'Failed to get artifact references' };
    }
  }

  // ==========================================================================
  // Repository Analysis (from githubAnalysisService.ts)
  // ==========================================================================

  /**
   * Analyze repository (comprehensive analysis)
   */
  static async analyzeRepository(
    projectId: string,
    userId: string
  ): Promise<CodebaseAnalysis | null> {
    try {
      const { data: project, error: projectError } = await supabase
        .from('projects')
        .select('github_owner, github_repo_name, github_token, default_branch')
        .eq('id', projectId)
        .maybeSingle();

      if (projectError) {
        logger.error('Error fetching project for repository analysis', {
          error: projectError,
          projectId,
        });
        return null;
      }

      if (!project?.github_owner || !project?.github_repo_name) {
        logger.error('Project missing GitHub metadata', { projectId, project });
        return null;
      }

      const { github_owner, github_repo_name, default_branch } = project;

      // Analyze repository structure
      const fileStructure = await this.getFileStructure(
        userId,
        github_owner,
        github_repo_name,
        default_branch || 'main'
      );

      // Analyze tech stack
      const techStack = await this.analyzeTechStack(
        userId,
        github_owner,
        github_repo_name,
        fileStructure
      );

      // Analyze architecture
      const architecture = await this.analyzeArchitecture(
        fileStructure,
        techStack
      );

      // Get dependencies
      const dependencies = await this.extractDependencies(
        userId,
        github_owner,
        github_repo_name
      );

      // Detect coding standards
      const standards = await this.detectCodingStandards(
        userId,
        github_owner,
        github_repo_name,
        fileStructure
      );

      return {
        techStack,
        architecture,
        dependencies,
        fileStructure,
        standards,
        totalFiles: fileStructure.length,
        totalLines: await this.countTotalLines(fileStructure),
      };
    } catch (error) {
      logger.error('Error analyzing repository', { error, projectId });
      return null;
    }
  }

  /**
   * Get file structure
   */
  private static async getFileStructure(
    userId: string,
    owner: string,
    repo: string,
    branch: string
  ): Promise<any[]> {
    try {
      const result = await this.makeGitHubRequest(
        userId,
        `/repos/${owner}/${repo}/git/trees/${branch}?recursive=1`,
        'GET'
      );

      if (result.success && result.data?.tree) {
        return result.data.tree.filter((item: any) => item.type === 'blob');
      }

      // Fallback: try common alternate default branch names
      const fallbackBranches =
        branch === 'main'
          ? ['master']
          : branch === 'master'
            ? ['main']
            : ['main', 'master'];
      for (const fb of fallbackBranches) {
        const fbResult = await this.makeGitHubRequest(
          userId,
          `/repos/${owner}/${repo}/git/trees/${fb}?recursive=1`,
          'GET'
        );
        if (fbResult.success && fbResult.data?.tree) {
          return fbResult.data.tree.filter((item: any) => item.type === 'blob');
        }
      }

      return [];
    } catch (error) {
      logger.error('Error getting file structure', {
        error,
        projectId: 'unknown',
      });
      return [];
    }
  }

  /**
   * Analyze tech stack
   */
  private static async analyzeTechStack(
    userId: string,
    owner: string,
    repo: string,
    fileStructure: any[]
  ): Promise<TechStackAnalysis> {
    const techStack: TechStackAnalysis = {};
    const filePaths = fileStructure.map(f => f.path);

    // Detect frontend framework
    if (filePaths.some(p => p.includes('package.json'))) {
      const pkgContent = await this.getFileContent(
        userId,
        owner,
        repo,
        'package.json'
      );

      if (pkgContent.success && pkgContent.content) {
        try {
          const pkg = JSON.parse(pkgContent.content);
          const deps = { ...pkg.dependencies, ...pkg.devDependencies };

          // Frontend detection
          if (deps.react) {
            techStack.frontend = {
              framework: 'React',
              version: deps.react,
              libraries: Object.keys(deps).filter(d => d.startsWith('react-')),
            };
          } else if (deps.vue) {
            techStack.frontend = {
              framework: 'Vue',
              version: deps.vue,
              libraries: Object.keys(deps).filter(d => d.startsWith('vue-')),
            };
          } else if (deps.angular || deps['@angular/core']) {
            techStack.frontend = {
              framework: 'Angular',
              version: deps['@angular/core'],
              libraries: Object.keys(deps).filter(d =>
                d.startsWith('@angular/')
              ),
            };
          }

          // Testing frameworks
          const testingFrameworks = [];
          if (deps.jest) testingFrameworks.push('Jest');
          if (deps.vitest) testingFrameworks.push('Vitest');
          if (deps.cypress) testingFrameworks.push('Cypress');
          if (deps.playwright) testingFrameworks.push('Playwright');

          if (testingFrameworks.length > 0) {
            techStack.testing = { frameworks: testingFrameworks };
          }

          // Dev tools
          techStack.devTools = {};
          if (deps.vite) techStack.devTools.bundler = 'Vite';
          else if (deps.webpack) techStack.devTools.bundler = 'Webpack';
          if (deps.eslint) techStack.devTools.linter = 'ESLint';
          if (deps.prettier) techStack.devTools.formatter = 'Prettier';
        } catch (error) {
          logger.error('Error parsing package.json', { error });
        }
      }
    }

    // Detect backend language and framework
    if (filePaths.some(p => p.includes('requirements.txt'))) {
      techStack.backend = {
        language: 'Python',
        libraries: [],
      };
    } else if (filePaths.some(p => p.includes('Gemfile'))) {
      techStack.backend = {
        language: 'Ruby',
        libraries: [],
      };
    } else if (filePaths.some(p => p.includes('go.mod'))) {
      techStack.backend = {
        language: 'Go',
        libraries: [],
      };
    } else if (filePaths.some(p => p.includes('pom.xml'))) {
      techStack.backend = {
        language: 'Java',
        libraries: [],
      };
    } else if (filePaths.some(p => p.includes('Cargo.toml'))) {
      techStack.backend = {
        language: 'Rust',
        libraries: [],
      };
    } else if (filePaths.some(p => p.endsWith('.php'))) {
      techStack.backend = {
        language: 'PHP',
        libraries: [],
      };
    }

    // Detect database
    const dbTypes = [];
    const orms = [];

    if (
      filePaths.some(p => p.includes('prisma') || p.includes('schema.prisma'))
    ) {
      orms.push('Prisma');
    }
    if (filePaths.some(p => p.includes('knexfile'))) {
      orms.push('Knex');
    }
    if (filePaths.some(p => p.includes('sequelize'))) {
      orms.push('Sequelize');
    }
    if (filePaths.some(p => p.includes('typeorm'))) {
      orms.push('TypeORM');
    }

    if (orms.length > 0 || dbTypes.length > 0) {
      techStack.database = {
        types: dbTypes,
        orms: orms,
      };
    }

    return techStack;
  }

  /**
   * Analyze architecture
   */
  private static async analyzeArchitecture(
    fileStructure: any[],
    _techStack: TechStackAnalysis
  ): Promise<ArchitectureInsights> {
    const filePaths = fileStructure.map(f => f.path);
    const designPatterns: string[] = [];
    const conventions: string[] = [];

    // Detect project structure
    let projectStructure = 'Unknown';
    if (filePaths.some(p => p.includes('src/components'))) {
      projectStructure = 'Component-based';
      conventions.push('Component organization');
    }
    if (filePaths.some(p => p.includes('src/pages'))) {
      projectStructure = 'Feature-based routing';
      conventions.push('Page-based routing');
    }
    if (filePaths.some(p => p.includes('src/features'))) {
      projectStructure = 'Feature-based';
      designPatterns.push('Feature-sliced design');
    }
    if (filePaths.some(p => p.includes('src/modules'))) {
      projectStructure = 'Module-based';
      designPatterns.push('Modular architecture');
    }

    // Detect design patterns
    if (filePaths.some(p => p.includes('/hooks/'))) {
      designPatterns.push('Custom Hooks');
    }
    if (filePaths.some(p => p.includes('/store/') || p.includes('/redux/'))) {
      designPatterns.push('State Management (Redux/Store)');
    }
    if (filePaths.some(p => p.includes('/context/'))) {
      designPatterns.push('Context API');
    }
    if (filePaths.some(p => p.includes('/services/'))) {
      designPatterns.push('Service Layer');
    }
    if (filePaths.some(p => p.includes('/utils/') || p.includes('/helpers/'))) {
      designPatterns.push('Utility Pattern');
    }

    // Detect file organization convention
    let fileOrganization = 'Mixed';
    if (filePaths.every(p => !p.includes('src/'))) {
      fileOrganization = 'Root-level organization';
    } else if (filePaths.some(p => p.startsWith('src/'))) {
      fileOrganization = 'Source directory organization';
    }

    return {
      projectStructure,
      designPatterns,
      conventions,
      fileOrganization,
    };
  }

  /**
   * Extract dependencies
   */
  private static async extractDependencies(
    userId: string,
    owner: string,
    repo: string
  ): Promise<Record<string, string>> {
    try {
      const pkgContent = await this.getFileContent(
        userId,
        owner,
        repo,
        'package.json'
      );

      if (pkgContent.success && pkgContent.content) {
        const pkg = JSON.parse(pkgContent.content);
        return {
          ...(pkg.dependencies || {}),
          ...(pkg.devDependencies || {}),
        };
      }
    } catch (error) {
      logger.error('Error extracting dependencies', { error });
    }

    return {};
  }

  /**
   * Detect coding standards
   */
  private static async detectCodingStandards(
    userId: string,
    owner: string,
    repo: string,
    fileStructure: any[]
  ): Promise<string[]> {
    const standards: string[] = [];
    const filePaths = fileStructure.map(f => f.path);

    // ESLint
    if (
      filePaths.some(
        p =>
          p.includes('.eslintrc') ||
          p === 'eslint.config.js' ||
          p === 'eslint.config.mjs'
      )
    ) {
      standards.push('ESLint configuration');
    }

    // Prettier
    if (
      filePaths.some(
        p => p.includes('.prettierrc') || p === 'prettier.config.js'
      )
    ) {
      standards.push('Prettier formatting');
    }

    // TypeScript
    if (filePaths.some(p => p === 'tsconfig.json')) {
      standards.push('TypeScript strict mode');
    }

    // EditorConfig
    if (filePaths.some(p => p === '.editorconfig')) {
      standards.push('EditorConfig');
    }

    // Git hooks
    if (filePaths.some(p => p.includes('.husky/') || p === '.huskyrc')) {
      standards.push('Git hooks (Husky)');
    }

    // Commit conventions
    if (
      filePaths.some(
        p => p.includes('commitlint') || p === 'commitlint.config.js'
      )
    ) {
      standards.push('Conventional Commits');
    }

    return standards;
  }

  /**
   * Count total lines
   */
  private static async countTotalLines(fileStructure: any[]): Promise<number> {
    return fileStructure.reduce((total, file) => {
      const estimatedLines = Math.floor((file.size || 0) / 30);
      return total + estimatedLines;
    }, 0);
  }

  // ==========================================================================
  // Repository Scaffolding (from githubScaffoldService.ts)
  // ==========================================================================

  /**
   * Scaffold repository with initial files
   */
  static async scaffoldRepository(
    userId: string,
    projectId: string,
    repoName: string,
    repoDescription: string,
    isPrivate: boolean,
    manifestData?: {
      files: Array<{
        path: string;
        content: string;
      }>;
    }
  ): Promise<{ success: boolean; repoUrl?: string; error?: string }> {
    try {
      // Create repository
      const createRepoResult = await this.createRepository(
        userId,
        repoName,
        repoDescription,
        isPrivate
      );

      if (!createRepoResult.success) {
        return {
          success: false,
          error: createRepoResult.error || 'Failed to create repository',
        };
      }

      const repoUrl = createRepoResult.repoUrl!;
      const owner = createRepoResult.owner!;

      // Update project with GitHub repo URL
      await supabase
        .from('projects')
        .update({
          repository_url: repoUrl,
          github_owner: owner,
          github_repo_name: repoName,
        })
        .eq('id', projectId);

      // Create develop branch
      const branchResult = await this.createBranch(userId, {
        owner,
        repo: repoName,
        branchName: 'develop',
        fromBranch: 'main',
      });

      if (!branchResult.success) {
        logger.warn('Failed to create develop branch:', branchResult.error);
      }

      // Scaffold initial files
      if (manifestData && manifestData.files.length > 0) {
        const scaffoldResult = await this.scaffoldInitialFiles(
          userId,
          owner,
          repoName,
          manifestData.files
        );

        if (!scaffoldResult.success) {
          logger.warn('Failed to scaffold some files:', scaffoldResult.error);
        }
      } else {
        // Create default README
        await this.createDefaultReadme(
          userId,
          owner,
          repoName,
          repoDescription
        );
      }

      return {
        success: true,
        repoUrl,
      };
    } catch (error) {
      logger.error('Error in scaffoldRepository:', error);
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  }

  /**
   * Create repository
   */
  private static async createRepository(
    userId: string,
    repoName: string,
    description: string,
    isPrivate: boolean
  ): Promise<{
    success: boolean;
    repoUrl?: string;
    owner?: string;
    error?: string;
  }> {
    try {
      const accessToken = await this.getAccessToken(userId);
      if (!accessToken) {
        return { success: false, error: 'GitHub not connected' };
      }

      const response = await fetch('https://api.github.com/user/repos', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/vnd.github.v3+json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: repoName,
          description,
          private: isPrivate,
          auto_init: true,
        }),
      });

      if (!response.ok) {
        const error = await response
          .json()
          .catch(() => ({ message: 'Unknown error' }));
        return {
          success: false,
          error: error.message || `GitHub API error: ${response.status}`,
        };
      }

      const repo = await response.json();

      return {
        success: true,
        repoUrl: repo.html_url,
        owner: repo.owner.login,
      };
    } catch (error) {
      logger.error('Error creating repository:', error);
      return {
        success: false,
        error: 'Failed to create repository',
      };
    }
  }

  /**
   * Scaffold initial files
   */
  private static async scaffoldInitialFiles(
    userId: string,
    owner: string,
    repo: string,
    files: Array<{ path: string; content: string }>
  ): Promise<{ success: boolean; error?: string }> {
    try {
      for (const file of files) {
        const result = await this.createFile(
          userId,
          owner,
          repo,
          file.path,
          file.content,
          `Initial commit: Add ${file.path}`,
          'main'
        );

        if (!result.success) {
          logger.warn(`Failed to create file ${file.path}:`, result.error);
        }
      }

      return { success: true };
    } catch (error) {
      logger.error('Error scaffolding files:', error);
      return { success: false, error: 'Failed to scaffold files' };
    }
  }

  /**
   * Create default README
   */
  private static async createDefaultReadme(
    userId: string,
    owner: string,
    repo: string,
    description: string
  ): Promise<void> {
    const readmeContent = `# ${repo}\n\n${description}\n\n---\n\n*Generated by [Cyclone Platform](https://cyclone.dev)*\n`;

    await this.createFile(
      userId,
      owner,
      repo,
      'README.md',
      readmeContent,
      'Initial commit: Add README.md',
      'main'
    );
  }

  // ==========================================================================
  // Project Import (from githubProjectImportService.ts)
  // ==========================================================================

  /**
   * Import existing GitHub project
   */
  static async importProject(
    userId: string,
    repoUrl: string,
    projectName?: string,
    description?: string,
    analyzeExistingCode?: boolean
  ): Promise<{ projectId: string; success: boolean; error?: string }> {
    // eslint-disable-next-line no-console
    console.log('[DEBUG] GitHubService.importProject called with:', {
      userId,
      repoUrl,
      projectName,
      analyzeExistingCode,
    });

    try {
      logger.info('Starting GitHub project import', {
        userId,
        repoUrl,
        projectName,
        analyzeExistingCode,
      });

      const repoInfo = this.parseGitHubUrlEnhanced(repoUrl);

      if (!repoInfo) {
        logger.error('Invalid GitHub repository URL', { repoUrl });
        return {
          projectId: '',
          success: false,
          error: 'Invalid GitHub repository URL',
        };
      }

      logger.info('Parsed repository URL', {
        owner: repoInfo.owner,
        repo: repoInfo.repo,
        fullName: repoInfo.fullName,
      });

      const accessToken = await this.getAccessToken(userId);
      if (!accessToken) {
        logger.error('No GitHub access token found', { userId });
        return {
          projectId: '',
          success: false,
          error:
            'GitHub connection not found. Please connect your GitHub account first.',
        };
      }

      logger.info('GitHub access token found, fetching repository details', {
        userId,
      });

      const repoDetails = await this.fetchRepoDetails(repoInfo, accessToken);

      if (!repoDetails.success) {
        const errorMessage =
          repoDetails.error || 'Failed to fetch repository details';
        const is404 =
          errorMessage.includes('404') || errorMessage.includes('Not Found');

        logger.error('Failed to fetch repository details', {
          error: repoDetails.error,
          is404,
          repoUrl,
        });

        return {
          projectId: '',
          success: false,
          error: is404
            ? `Repository not found or access denied. Please verify:
               - The repository exists at: ${repoUrl}
               - Your GitHub token has access to this repository
               - The repository is not private (or your token has private repo access)`
            : errorMessage,
        };
      }

      logger.info('Repository details fetched successfully', {
        defaultBranch: repoDetails.defaultBranch,
        language: repoDetails.language,
      });

      const finalProjectName = projectName || repoInfo.repo;
      const finalDescription = description || repoDetails.description || '';

      logger.info('Creating project in database', {
        projectName: finalProjectName,
        userId,
        repoUrl,
      });

      const { data: project, error: projectError } = await supabase
        .from('projects')
        .insert({
          user_id: userId,
          name: finalProjectName,
          description: finalDescription,
          repository_url: repoUrl,
          default_branch: repoDetails.defaultBranch || 'main',
          github_owner: repoInfo.owner,
          github_repo_name: repoInfo.repo,
        })
        .select('id')
        .single();

      if (projectError || !project) {
        logger.error('Failed to create project in database', {
          error: projectError,
          projectName: finalProjectName,
          userId,
        });
        return {
          projectId: '',
          success: false,
          error: projectError?.message || 'Failed to create project',
        };
      }

      logger.info('Project created successfully', {
        projectId: project.id,
        projectName: finalProjectName,
      });

      // Analyze codebase if requested
      let analysisError: string | undefined;
      if (analyzeExistingCode) {
        logger.info('Starting codebase analysis', { projectId: project.id });

        try {
          // Import analysis will be handled by IntelligentImportService
          const { IntelligentImportService } = await import(
            '../intelligentImportService'
          );

          logger.info(
            'Calling IntelligentImportService.analyzeAndImportRepository',
            {
              projectId: project.id,
              repoFullName: repoInfo.fullName,
            }
          );

          await IntelligentImportService.analyzeAndImportRepository({
            userId,
            projectId: project.id,
            repoUrl,
            repoFullName: repoInfo.fullName,
            accessToken,
          });

          logger.info('Codebase analysis completed successfully', {
            projectId: project.id,
          });
        } catch (analysisErr) {
          const errorMessage =
            analysisErr instanceof Error
              ? analysisErr.message
              : String(analysisErr);

          logger.error('Error during codebase analysis', {
            error: analysisErr,
            projectId: project.id,
            errorMessage,
          });

          // Store error message but don't fail the import - project is created, analysis can be retried
          analysisError = `Analysis failed: ${errorMessage}`;

          // Log the full error for debugging
          console.error('[GitHub Import] Analysis failed:', analysisErr);
        }
      }

      logger.info('GitHub project import completed', {
        projectId: project.id,
        analysisPerformed: !!analyzeExistingCode,
        analysisError: !!analysisError,
      });

      return {
        projectId: project.id,
        success: true,
        error: analysisError, // Include analysis error in response if it occurred
      };
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      logger.error('GitHub project import failed', {
        error,
        errorMessage,
        userId,
        repoUrl,
        projectName,
      });

      // Also log to console for immediate debugging
      console.error('[GitHub Import] Import failed:', error);

      return {
        projectId: '',
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Parse GitHub URL (enhanced version with fullName)
   */
  static parseGitHubUrlEnhanced(url: string): {
    owner: string;
    repo: string;
    fullName: string;
    defaultBranch: string;
  } | null {
    const patterns = [
      /github\.com\/([^/]+)\/([^/]+)/,
      /github\.com:([^/]+)\/([^/]+)\.git/,
    ];

    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) {
        const owner = match[1];
        const repo = match[2].replace(/\.git$/, '');

        return {
          owner,
          repo,
          fullName: `${owner}/${repo}`,
          defaultBranch: 'main',
        };
      }
    }

    return null;
  }

  /**
   * Fetch repository details
   */
  private static async fetchRepoDetails(
    repoInfo: { owner: string; repo: string; fullName: string },
    accessToken: string
  ): Promise<{
    success: boolean;
    description?: string;
    defaultBranch?: string;
    language?: string;
    stars?: number;
    forks?: number;
    error?: string;
  }> {
    try {
      const response = await fetch(
        `https://api.github.com/repos/${repoInfo.fullName}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: 'application/vnd.github+json',
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const errorMessage = errorData.message || response.statusText;

        if (response.status === 404) {
          return {
            success: false,
            error: `Repository not found (404). ${errorMessage}`,
          };
        }

        return {
          success: false,
          error: `GitHub API error: ${response.status} - ${errorMessage}`,
        };
      }

      const data = await response.json();

      return {
        success: true,
        description: data.description || '',
        defaultBranch: data.default_branch || 'main',
        language: data.language || undefined,
        stars: data.stargazers_count || 0,
        forks: data.forks_count || 0,
      };
    } catch (error) {
      logger.error('Error fetching repo details:', error);
      return {
        success: false,
        error: 'Failed to fetch repository details',
      };
    }
  }

  /**
   * Detect frontend framework from dependencies
   */
  private static detectFrontendFramework(
    dependencies: Record<string, string>
  ): string {
    if (dependencies['react']) return 'React';
    if (dependencies['vue']) return 'Vue';
    if (dependencies['@angular/core']) return 'Angular';
    if (dependencies['svelte']) return 'Svelte';
    if (dependencies['next']) return 'Next.js';
    return 'Unknown';
  }
}

// ============================================================================
// Singleton Export
// ============================================================================

// Export singleton instance with lazy initialization to avoid TDZ errors
let _githubService: GitHubService | null = null;
const getGitHubService = (): GitHubService => {
  if (!_githubService) {
    _githubService = new GitHubService();
  }
  return _githubService;
};

export const githubService = new Proxy({} as GitHubService, {
  get(_target, prop) {
    const instance = getGitHubService();
    const value = (instance as any)[prop];
    if (typeof value === 'function') {
      return value.bind(instance);
    }
    return value;
  },
  set(_target, prop, value) {
    const instance = getGitHubService();
    (instance as any)[prop] = value;
    return true;
  },
  has(_target, prop) {
    const instance = getGitHubService();
    return prop in instance;
  },
});
